/**
 * Complete dashboard filters data for POST request with all required fields.
 * Same rules as `completeTableFiltersForPost`, restricted to filterValues / search / personalViews:
 * the personal view is only kept when the handler sends it (any other change deselects it).
 * Cards are only sent when the handler sends them: an absent `cards` keeps the layout.
 * @param {Partial<ApiManagerDashboardFiltersCreate>} partialData - Partial data from handler
 * @param {ApiManagerDashboardFilters | undefined} currentFilters - Current dashboard filters from GET
 * @returns {ApiManagerDashboardFiltersCreate} Complete data with all required fields
 */
export const completeDashboardFiltersForPost = (partialData, currentFilters) => ({
  filterValues: partialData?.filterValues ?? currentFilters?.filterValues ?? [],
  search: {
    // The GET may return null for search or selectedOptions, but the POST requires an array
    selectedOptions: (() => {
      const value = partialData?.search?.selectedOptions ?? currentFilters?.search?.selectedOptions;
      if (!Array.isArray(value)) return [];
      return value.filter((item) => item != null);
    })(),
    searchValue: partialData?.search?.searchValue ?? currentFilters?.search?.searchValue ?? '',
  },
  personalViews: {
    selectedOption: partialData?.personalViews?.selectedOption ?? null,
  },
  ...(partialData?.cards ? { cards: partialData.cards } : {}),
});

/**
 * Whether the dashboard has something to display in its filter bar: borrowed filters, search, views,
 * or a customizable cards layout (a customizable dashboard without filters still needs the bar for its views).
 * A dashboard that borrows nothing and is not customizable gets an empty payload (cards null).
 * @param {ApiManagerDashboardFilters | undefined} dashboardFilters - Dashboard filters from GET
 * @returns {boolean} True when the filter bar has at least one block to show
 */
export const hasDashboardFilters = (dashboardFilters) => (
  (dashboardFilters?.filters?.length ?? 0) > 0
  || (dashboardFilters?.search?.options?.length ?? 0) > 0
  || (dashboardFilters?.personalViews?.options?.length ?? 0) > 0
  || Array.isArray(dashboardFilters?.cards)
);

/**
 * Whether the dashboard cards are being reloaded because of a filter change:
 * from the dashboard filters POST until /dashboard has been read again after it.
 * @param {object} params
 * @param {boolean} params.isFiltersPending - True while the dashboard filters POST is pending
 * @param {boolean} params.isDashboardFetching - True while /dashboard is being fetched
 * @param {number} params.dashboardUpdatedAt - Timestamp of the last /dashboard data (0 if none)
 * @param {number} params.filtersSubmittedAt - Timestamp of the last filters POST (0 if none)
 * @returns {boolean} True when the cards should show their skeleton
 */
export const isDashboardReloadingAfterFilters = ({
  isFiltersPending,
  isDashboardFetching,
  dashboardUpdatedAt,
  filtersSubmittedAt,
}) => {
  if (isFiltersPending) return true;
  if (!filtersSubmittedAt) return false;
  return isDashboardFetching && dashboardUpdatedAt < filtersSubmittedAt;
};

/**
 * Optimistic cards layout after a dashboard filters POST: the posted places and visibility are applied
 * to the known cards. Without posted cards (layout unchanged) or on reset (empty array, default layout
 * computed by the API), the previous cards are kept until the GET is read again.
 * @param {Array<ApiManagerDashboardFilterCard> | null | undefined} previousCards - Cards from the last GET
 * @param {Array<ApiManagerDashboardFiltersCreateCard> | null | undefined} postedCards - Cards from the POST body
 * @returns {Array<ApiManagerDashboardFilterCard> | null | undefined} The optimistic cards
 */
export const mergeDashboardFilterCards = (previousCards, postedCards) => {
  if (!Array.isArray(previousCards) || !postedCards?.length) return previousCards;
  const postedById = new Map(postedCards.map((card) => [card.id, card]));
  return previousCards.map((card) => {
    const posted = postedById.get(card.id);
    if (!posted) return card;
    return {
      ...card,
      column: posted.column,
      position: posted.position,
      visible: posted.visible,
    };
  });
};
